import { Skeleton } from 'antd';
import type { ReactNode } from 'react';

type GlassCardTheme =
  | 'income'
  | 'payables'
  | 'wages'
  | 'balance'
  | 'balanceAmberSoft'
  | 'dangerSoft'
  | 'success'
  | 'info';

type GlassCardProps = {
  title: ReactNode;
  description?: ReactNode;
  body?: ReactNode;
  icon?: ReactNode;
  iconBgColor?: string;
  theme?: GlassCardTheme;
  className?: string;
  isLoading?: boolean;
  bodyClassName?: string;
  titleClassName?: string;
};

const themeStyles: Record<
  GlassCardTheme,
  {
    background: string;
    borderColor: string;
    accent: string;
    accentBg: string;
    text: string;
  }
> = {
  income: {
    background: 'linear-gradient(135deg, #f5fbf7, #f9fcfa)',
    borderColor: 'rgba(16,185,129,0.16)',
    accent: 'text-emerald-700',
    accentBg: 'bg-emerald-500',
    text: 'text-slate-900',
  },
  payables: {
    background: 'linear-gradient(135deg, #f6f8ff, #fbfdff)',
    borderColor: 'rgba(59,130,246,0.16)',
    accent: 'text-blue-700',
    accentBg: 'bg-blue-600',
    text: 'text-slate-900',
  },
  wages: {
    background: 'linear-gradient(135deg, #f4fbf6, #f9fdfb)',
    borderColor: 'rgba(59,130,246,0.14)',
    accent: 'text-sky-600',
    accentBg: 'bg-sky-500',
    text: 'text-slate-900',
  },
  balance: {
    background: 'linear-gradient(135deg, #f9fbfd, #fcfdfd)',
    borderColor: 'rgba(148,163,184,0.12)',
    accent: 'text-slate-600',
    accentBg: 'bg-slate-500',
    text: 'text-slate-900',
  },
  balanceAmberSoft: {
    background: 'linear-gradient(135deg, #fffdf8, #fffefc)',
    borderColor: 'rgba(245,158,11,0.12)',
    accent: 'text-amber-500',
    accentBg: 'bg-amber-300',
    text: 'text-slate-900',
  },
  dangerSoft: {
    background: 'linear-gradient(135deg, #fff8f8, #fffdfd)',
    borderColor: 'rgba(225,29,72,0.12)',
    accent: 'text-rose-600',
    accentBg: 'bg-rose-500',
    text: 'text-slate-900',
  },
  success: {
    background: 'linear-gradient(135deg, #f4fbf7, #f8fcfa)',
    borderColor: 'rgba(16,185,129,0.12)',
    accent: 'text-emerald-700',
    accentBg: 'bg-emerald-500',
    text: 'text-slate-900',
  },
  info: {
    background: 'linear-gradient(135deg, #f4f7ff, #f9fbff)',
    borderColor: 'rgba(14,165,233,0.14)',
    accent: 'text-sky-700',
    accentBg: 'bg-sky-500',
    text: 'text-slate-900',
  },
};

const GlassCard = ({
  title,
  description,
  body,
  icon,
  iconBgColor,
  theme = 'income',
  className = '',
  bodyClassName = '',
  titleClassName = '',
  isLoading = false,
}: GlassCardProps) => {
  const currentTheme = themeStyles[theme];
  const iconBackgroundStyle = iconBgColor ? { backgroundColor: iconBgColor } : undefined;
  const iconBackgroundClassName = iconBgColor ? '' : currentTheme.accentBg;

  return (
    <div
      className={`
        relative flex w-full flex-col gap-3 rounded-xl border p-5 text-left
        shadow-[0_1px_4px_rgba(0,0,0,0.03)]
        ${className}
      `}
      style={{
        background: currentTheme.background,
        borderColor: currentTheme.borderColor,
      }}
    >
      <div className="flex items-start justify-between gap-4">
        <div className={`min-w-0 font-semibold text-xl ${currentTheme.text} ${titleClassName}`.trim()}>
          {isLoading ? <Skeleton active title={{ width: 160 }} paragraph={false} /> : title}
        </div>

        {icon && (
          <div
            className={`
              flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-lg text-white
              ${iconBackgroundClassName} shadow-sm
            `}
            style={iconBackgroundStyle}
          >
            {icon}
          </div>
        )}
      </div>

      <div className={`w-full ${bodyClassName}`.trim()}>
        {isLoading ? (
          <div className="pt-1">
            <Skeleton active title={{ width: 120 }} paragraph={false} />
          </div>
        ) : (
          body && <div className="text-3xl font-semibold text-slate-900">{body}</div>
        )}
      </div>

      <div className="w-full">
        {isLoading ? (
          <Skeleton active title={false} paragraph={{ rows: 1, width: '70%' }} />
        ) : (
          description && <div className="text-sm text-slate-500">{description}</div>
        )}
      </div>
    </div>
  );
};

export default GlassCard;
